import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import apiServices from "../../service";

interface FileState {
  id: string;
  fileName: string;
  fileSize: number;
  status: "idle" | "scanning" | "scanned" | "uploading" | "success" | "error";
  error?: string;
  serverFileId?: string;
}

interface FileUploadState {
  files: FileState[];
  loading: boolean;
}

const initialState: FileUploadState = {
  files: [],
  loading: false,
};


/**
 * API: UPLOAD FILE
 * Sends the cleared file for getting objectId.
 * Author: Siyad S
 */
// Add signal to the Thunk arguments
export const uploadFile = createAsyncThunk<
  { data: { objectId: string }[]; message: string; statusCode: number; isError: boolean },
  { file: File; tempId: string; userId: string; signal?: AbortSignal },
  { rejectValue: string }
>(
  "fileUpload/uploadFile",
  async ({ file, userId, signal }, { rejectWithValue }) => {
    try {
      const response = await apiServices.FileUpload.upload(file, userId, signal);
      return response.data; // response.data is already { data: [{objectId}], message, ... }
    } catch (err: any) {
      if (err.name === "CanceledError")
        return rejectWithValue("Upload cancelled");
      const serverError =
        err.response?.data?.error?.message?.[0] ||
        err.response?.data?.message ||
        "Upload failed";
      return rejectWithValue(serverError);
    }
  },
);

export const scanFile = createAsyncThunk<
  { scanStatus: string; isError: boolean; error: { message: string[] } },
  { objectId: string; tempId: string; signal?: AbortSignal }, // Added signal here
  { rejectValue: string }
>("fileUpload/scanFile", async ({ objectId, signal }, { rejectWithValue }) => {
  try {
    const response = await apiServices.FileUpload.scanStatus(objectId, signal);
    return response.data.data[0];
  } catch (err: any) {
    if (err.name === "CanceledError") return rejectWithValue("Scan cancelled");
    return rejectWithValue(err.response?.data?.message || "Scan check failed");
  }
});

const fileUploadSlice = createSlice({
  name: "file_upload",
  initialState,
  reducers: {
    addFiles: (state, action: PayloadAction<File[]>) => {
      const newFiles = action.payload.map((f) => ({
        id: `${f.name}-${Date.now()}`,
        fileName: f.name, // Store the string name
        fileSize: f.size, // Store the number size
        status: "idle" as const,
      }));
      state.files.push(...newFiles);
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.files = state.files.filter((f) => f.id !== action.payload);

      // If no files are left or none are in a 'busy' state, reset loading
      const busyFiles = state.files.filter(
        (f) => f.status === "uploading" || f.status === "scanning",
      );
      if (busyFiles.length === 0) {
        state.loading = false;
      }
    },
    clearAllFiles: (state) => {
      state.files = [];
      state.loading = false;
    },
    clearLoading: (state) => {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadFile.pending, (state, action) => {
        state.loading = true;
        const { tempId, file } = action.meta.arg;

        const existingFile = state.files.find((f) => f.id === tempId);
        if (!existingFile) {
          state.files.push({
            id: tempId,
            fileName: file.name, // Serializable string
            fileSize: file.size, // Serializable number
            status: "uploading",
          });
        } else {
          existingFile.status = "uploading";
        }
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        const file = state.files.find((f) => f.id === action.meta.arg.tempId);
        if (file) {
          file.status = "uploading";
          file.serverFileId = action.payload.data?.[0]?.objectId;
        }
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.loading = false;
        const file = state.files.find((f) => f.id === action.meta.arg.tempId);
        if (file) {
          file.status = "error";
          file.error = action.payload || "Upload failed";
        }
      })
      .addCase(scanFile.pending, (state, action) => {
        const file = state.files.find((f) => f.id === action.meta.arg.tempId);
        if (file) {
          file.status = "scanning";
        }
        state.loading = true;
      })

      .addCase(scanFile.fulfilled, (state, action: any) => {
        const file = state.files.find((f) => f.id === action.meta.arg.tempId);
        if (file) {
          const apiScanResult = action.payload?.scanStatus;
          const isApiError = action.payload?.isError; // The 500 error flag

          if (apiScanResult === "SUCCESS") {
            state.loading = false;
            file.status = "success";
          }
          // Handle the specific 500 error structure you shared
          else if (
            apiScanResult === "FAILED" ||
            apiScanResult === "ERROR" ||
            isApiError
          ) {
            state.loading = false;
            file.status = "error";
            // Extract "Please Contact Administrator" from the nested array
            file.error =
              action.payload?.error?.message?.[0] ||
              "Security scan failed. Please contact administrator.";
          } else {
            file.status = "scanning";
          }
        }
      })

      .addCase(scanFile.rejected, (state, action) => {
        state.loading = false;
        const file = state.files.find((f) => f.id === action.meta.arg.tempId);
        if (file) {
          file.status = "error";
          file.error =
            (action.payload as string) || "Network error during scan";
        }
      });
  },
});

export const { addFiles, removeFile, clearAllFiles, clearLoading } = fileUploadSlice.actions;
export default fileUploadSlice.reducer;
